import { defineStore } from 'pinia';
import { STORAGE_KEYS, LANGUAGE_OPTIONS } from '@/shared/constants/app';
import { useAppStore } from '@/core/stores/app.store';
import { setI18nLocale } from '@/core/i18n';
import storageService from '@/shared/services/storage.service';
import pushSessionService from '@/shared/services/push-session.service';
import environmentService from '@/shared/services/environment.service';
import { APP_ONBOARDING_STATUS } from '@/shared/constants/authentication';

const DEFAULT_LANGUAGE_CODE = LANGUAGE_OPTIONS[0].code;
let initAuthPromise = null;
let pendingStorageWrite = Promise.resolve();

// Serialize local writes so an older Capacitor write cannot restore a session
// after logout. State ownership is checked again when each write reaches the queue.
const queueSessionStorageWrite = (action) => {
  const result = pendingStorageWrite.then(action, action);
  // A failed operation still reaches its caller, but must not block later cleanup.
  pendingStorageWrite = result.catch(() => {});
  return result;
};

const clearStoredSession = async () => {
  const results = await Promise.allSettled([
    storageService.removeItem(STORAGE_KEYS.TOKEN),
    storageService.removeItem(STORAGE_KEYS.PATIENT_MAIN_UNIQUE),
  ]);

  // Both removals must be attempted, even if one storage operation fails.
  const hasFailedRemoval = results.some((result) => result.status === 'rejected');
  if (hasFailedRemoval) {
    throw new Error('Unable to clear stored session');
  }
};

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    account: {},
    pendingPasswordChange: null,
    // Local response ownership only; sessionVersion is the API's credential identifier.
    authStateVersion: 0,
    isAuthReady: false,
  }),
  getters: {
    isLoggedIn: (state) => Boolean(state.account?.mainUnique && state.account?.token),
    isTreatmentUnavailable: (state) =>
      Boolean(state.account?.token && state.account.hasActiveTreatment === false),
    token: (state) => state.account?.token,
    ME: (state) => state.account,
    actor: (state) => state.account.actor,
    isActingAsCaregiver: (state) => Boolean(state.account.caregiverMainUnique),
    patientAvatar: (state) => state.account?.avatar || '',
    patientDisplayName: (state) => String(state.account?.patientName || '').trim(),
    // The signed-in person: the caregiver when acting for a patient, otherwise the patient.
    userDisplayName(state) {
      if (state.account.caregiverMainUnique) {
        return String(state.account.actor?.patientName || '').trim();
      }
      return this.patientDisplayName;
    },
    userAvatar(state) {
      if (state.account.caregiverMainUnique) {
        return state.account.actor?.avatar || '';
      }
      return this.patientAvatar;
    },
    appOnboardingStatus: (state) => state.account.appOnboarding?.status,
  },
  actions: {
    // Invalidate pending responses whenever the login or viewed account changes.
    beginAuthentication() {
      this.authStateVersion += 1;
      this.pendingPasswordChange = null;
      return this.authStateVersion;
    },

    setPasswordChallenge(payload, authStateVersion, username) {
      // Ignore a login response received after the user started another auth flow.
      if (authStateVersion !== this.authStateVersion) {
        return false;
      }

      // A challenge needs a token and expiry, but cannot authorize App requests.
      if (!payload?.passwordChangeToken) {
        throw new Error('Invalid password change response');
      }

      const hasValidExpiry = Number.isFinite(payload.expiresIn) && payload.expiresIn > 0;
      if (!hasValidExpiry) {
        throw new Error('Invalid password change response');
      }

      // This state is not persisted and is never sent as Authorization.
      this.pendingPasswordChange = {
        token: payload.passwordChangeToken,
        expiresAt: Date.now() + payload.expiresIn * 1000,
        reason: payload.reason,
        passwordLastSetTime: payload.passwordLastSetTime,
        username,
      };
      this.isAuthReady = true;
      return true;
    },

    async setSession(session, authStateVersion = this.authStateVersion) {
      // An incomplete response must not become a locally authenticated account.
      if (
        !session?.token ||
        !session.mainUnique ||
        !Object.values(APP_ONBOARDING_STATUS).includes(session.appOnboarding?.status)
      ) {
        throw new Error('Invalid session response');
      }

      // Publish the session only after its storage writes and locale have succeeded.
      return queueSessionStorageWrite(async () => {
        if (authStateVersion !== this.authStateVersion) {
          return false;
        }

        try {
          // Persist both identifiers before making patient screens available.
          await storageService.setItem(STORAGE_KEYS.TOKEN, session.token);
          await storageService.setItem(STORAGE_KEYS.PATIENT_MAIN_UNIQUE, session.mainUnique);

          // An account switch during native storage writes cancels this session.
          if (authStateVersion !== this.authStateVersion) {
            await clearStoredSession();
            return false;
          }

          // Before setup completes, keep the language chosen on Login. The
          // profile-language endpoint is available only after onboarding.
          if (session.appOnboarding.status === APP_ONBOARDING_STATUS.COMPLETED) {
            await setI18nLocale(session.language ?? DEFAULT_LANGUAGE_CODE);
          }

          // Locale loading is asynchronous too; ownership must still match.
          if (authStateVersion !== this.authStateVersion) {
            await clearStoredSession();
            return false;
          }

          // Commit the in-memory session only while this auth flow still owns it.
          this.account = session;
          this.pendingPasswordChange = null;
          this.isAuthReady = true;
          return true;
        } catch (error) {
          // Do not leave a partially persisted session after storage or locale failure.
          this.account = {};
          await clearStoredSession();
          throw error;
        }
      });
    },

    async signIn(session, authStateVersion = this.authStateVersion, language) {
      if (authStateVersion !== this.authStateVersion) {
        return false;
      }

      if (
        language &&
        session?.token &&
        session.mainUnique &&
        session.appOnboarding?.status === APP_ONBOARDING_STATUS.COMPLETED &&
        session.language !== language
      ) {
        // Use the verified token without publishing the session or starting
        // patient requests until its chosen language has been saved.
        const { default: authService } = await import('@/shared/services/auth.service');
        if (authStateVersion !== this.authStateVersion) {
          return false;
        }
        session = await authService.updateProfile(
          { language },
          {
            headers: { Authorization: `Bearer ${session.token}` },
            params: { lang: language },
            silent: true,
            publicAuth: true,
          },
        );
      }

      // Session persistence must succeed before any login side effects start.
      const isSessionStored = await this.setSession(session, authStateVersion);
      if (!isSessionStored) {
        return false;
      }

      // Terms and setup must finish before asking for notification access.
      if (
        this.isTreatmentUnavailable ||
        session.appOnboarding.status !== APP_ONBOARDING_STATUS.COMPLETED
      ) {
        return true;
      }

      // Register notifications only for the session that was actually adopted.
      const apiUrl = environmentService.getApiUrlSync();
      queueMicrotask(async () => {
        const isCurrentSession =
          authStateVersion === this.authStateVersion && this.token === session.token;
        if (!isCurrentSession) {
          return;
        }

        try {
          await pushSessionService.registerForSession({
            token: session.token,
            apiUrl,
            registrationId: `${authStateVersion}:${session.mainUnique}`,
            isCurrent: () => authStateVersion === this.authStateVersion && this.isLoggedIn,
          });
        } catch {
          console.warn('Notification registration failed');
        }
      });

      return true;
    },

    requireOnboarding(status, requestToken) {
      if (requestToken !== this.token || !this.isLoggedIn) {
        return false;
      }
      if (
        ![
          APP_ONBOARDING_STATUS.PENDING_TERMS_ACCEPTANCE,
          APP_ONBOARDING_STATUS.IN_PROGRESS,
        ].includes(status)
      ) {
        throw new Error('Invalid onboarding response');
      }
      this.account.appOnboarding = { ...this.account.appOnboarding, status };
      return true;
    },

    async signOut() {
      // Capture the outgoing token and invalidate responses still in flight.
      const hadSession = this.isLoggedIn;
      const sessionToken = this.token;
      const apiUrl = environmentService.getApiUrlSync();
      this.beginAuthentication();

      // Start push cleanup with the old token, but end local authorization now,
      // even when network cleanup fails.
      if (hadSession) {
        void pushSessionService
          .unregisterForSession({ token: sessionToken, apiUrl })
          .catch(() => console.warn('Notification cleanup failed'));
      }

      this.account = {};
      this.isAuthReady = true;

      // Queue local cleanup behind any earlier writes from the retired session.
      await queueSessionStorageWrite(async () => {
        await clearStoredSession();
      });
    },

    async initAuth() {
      // Startup, foreground return and route guards share one validation request.
      if (initAuthPromise) {
        return initAuthPromise;
      }
      initAuthPromise = this.runInitAuth();

      try {
        await initAuthPromise;
      } finally {
        initAuthPromise = null;
      }
    },

    async runInitAuth() {
      // Tie validation to the current auth flow, not a later login.
      const authStateVersion = this.authStateVersion;
      const wasLoggedIn = this.isLoggedIn;
      this.isAuthReady = false;

      try {
        // A stored token is only a candidate; it cannot open patient screens yet.
        const token = this.token || (await storageService.getItem(STORAGE_KEYS.TOKEN));
        if (authStateVersion !== this.authStateVersion) {
          return;
        }
        if (!token) {
          return;
        }

        // The stored token is provisional until /me validates and refreshes it.
        // Keep mounted forms during resume validation; only cold start needs a candidate account.
        if (!wasLoggedIn) {
          this.account = { token };
        }
        // Lazy import breaks a circular dependency: auth.service -> base.service ->
        // repository.factory -> ...repositories -> rest-client -> this store -> auth.service.
        const { default: authService } = await import('@/shared/services/auth.service');
        const session = await authService.getMe();
        if (authStateVersion !== this.authStateVersion) {
          return;
        }

        // Foreground return must also notice notification permission enabled in Settings.
        const isSessionStored = await this.signIn(session, authStateVersion);
        if (isSessionStored) {
          useAppStore().setDateTimeConfig(session.config?.clientDateTimeFormatConfig);
        }
      } catch {
        // An unverified token cannot open authenticated screens.
        if (authStateVersion === this.authStateVersion) {
          await this.signOut();
        }
      } finally {
        if (authStateVersion === this.authStateVersion) {
          this.isAuthReady = true;
        }
      }
    },
  },
});
